import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Sparkles, ArrowRight, MessageSquare, Image, Video, Music,
  Bot, Code, Languages, Table, ChevronRight, Star, Shield, Zap, Globe,
  Check, Users, Brain, Loader2, ChevronDown
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import SEO from '../components/SEO'
import client from '../api/client'

interface LandingBlock {
  id: number
  section: string
  block_key: string
  block_type: string
  content: string | null
  media_url: string | null
  media_alt: string | null
  icon_name: string | null
  format_type: string | null
  is_bold: boolean
  extra_data: string | null
  sort_order: number
  is_visible: boolean
}

// Lucide icon resolver
const lucideIcons: Record<string, any> = {
  MessageSquare, Image, Video, Music, Bot, Code,
  Zap, Globe, Brain, Shield, Star, Users, Sparkles,
}

// Built-in fallback data when API is not available
const fallbackServices = [
  { icon: <MessageSquare className="w-6 h-6" />, title: 'Чат с нейросетью', path: '/chat', desc: 'GPT-5, Claude, DeepSeek, Grok — 28 языковых моделей для общения и работы', color: 'from-emerald-500 to-teal-500', borderColor: 'border-emerald-500/30', bgColor: 'bg-emerald-500/5' },
  { icon: <Image className="w-6 h-6" />, title: 'Генерация изображений', path: '/image', desc: 'Flux, Recraft, Seedream — создавайте картинки по описанию на русском', color: 'from-amber-500 to-orange-500', borderColor: 'border-amber-500/30', bgColor: 'bg-amber-500/5' },
  { icon: <Video className="w-6 h-6" />, title: 'Генерация видео', path: '/video', desc: 'Kling, Seedance, Grok Imagine — текст и картинку в видео', color: 'from-red-500 to-rose-500', borderColor: 'border-red-500/30', bgColor: 'bg-red-500/5' },
  { icon: <Music className="w-6 h-6" />, title: 'Генерация музыки', path: '/music', desc: 'Suno, Udio, ElevenLabs — создавайте треки и песни по тексту', color: 'from-pink-500 to-fuchsia-500', borderColor: 'border-pink-500/30', bgColor: 'bg-pink-500/5' },
  { icon: <Bot className="w-6 h-6" />, title: 'AI Персонажи', path: '/characters', desc: 'Эйнштейн, Шерлок Холмс, Фрейд — общайтесь с уникальными нейросетями', color: 'from-purple-500 to-violet-500', borderColor: 'border-purple-500/30', bgColor: 'bg-purple-500/5' },
  { icon: <Code className="w-6 h-6" />, title: 'Помощник кода', path: '/code', desc: 'Написание, отладка и оптимизация кода с AI на Python, JS, Go', color: 'from-blue-500 to-cyan-500', borderColor: 'border-blue-500/30', bgColor: 'bg-blue-500/5' },
  { icon: <Bot className="w-6 h-6" />, title: 'ИИ-Агенты', path: '/agents', desc: 'Deep Research, анализ данных, веб-поиск — автоматизация с AI', color: 'from-indigo-500 to-purple-500', borderColor: 'border-indigo-500/30', bgColor: 'bg-indigo-500/5' },
  { icon: <Languages className="w-6 h-6" />, title: 'AI Переводчик', path: '/translator', desc: 'Перевод, сжатие текста и суммаризация YouTube видео нейросетью', color: 'from-teal-500 to-emerald-500', borderColor: 'border-teal-500/30', bgColor: 'bg-teal-500/5' },
  { icon: <Table className="w-6 h-6" />, title: 'AI Таблицы', path: '/sheets', desc: 'Создавайте таблицы, CSV и HTML по текстовому описанию', color: 'from-green-500 to-lime-500', borderColor: 'border-green-500/30', bgColor: 'bg-green-500/5' },
]

const fallbackFeatures = [
  { icon: Globe, title: 'Все нейросети в одном окне', desc: 'GPT, Claude, DeepSeek, Grok, Flux, Kling, Suno — десятки AI моделей на одной платформе без десятка регистраций' },
  { icon: Zap, title: 'Бесплатные модели безлимитно', desc: 'Chat-Neuro.ru — нейросеть онлайн бесплатно. GPT-5, Claude Sonnet, Gemini, DeepSeek доступны без ограничений' },
  { icon: Brain, title: 'Нейросеть на русском языке', desc: 'Все AI модели понимают и отвечают на русском. Чат, генерация картинок, видео, музыки — всё на родном языке' },
  { icon: Shield, title: 'Единая система токенов', desc: 'CN-токены для чата и звёзды для генерации изображений, видео и музыки. Прозрачные цены без скрытых платежей' },
]

const fallbackPlans = [
  { id: 'free', name: 'Бесплатный', price: '0', desc: 'Для знакомства с платформой', tokens: 0, stars: 0, free: true, features: ['GPT-5.2, GPT-5.1', 'Claude Sonnet 4', 'Gemini 2.5 Pro', 'DeepSeek V4', 'Grok 4'], services: ['Доступ к 6 AI-моделям бесплатно и безлимитно', 'Голосовой ввод и вывод', 'Чат', 'Работа с таблицами и документами', 'Переводчик, Компрессор текста, YouTube суммаризатор', 'Уникальные AI-персонажи', 'AI Помощник по коду', 'ИИ-Агенты'], popular: false },
  { id: 'starter', name: 'Начальный', price: '499', desc: 'Для повседневных задач', tokens: 7_000_000, stars: 700, features: ['7 000 000 CN-токенов', '700 ⭐ для генерации медиа', 'Загрузка и обработка файлов', 'Поиск в интернете', 'Deep Research (глубокий анализ)', 'Голосовой ввод и вывод', 'Все топовые AI-модели', 'Генерация картинок, видео и музыки', 'ИИ-агенты для сложных задач'], popular: false },
  { id: 'standard', name: 'Стандарт', price: '999', desc: 'Для активных пользователей', tokens: 14_000_000, stars: 1_400, features: ['14 000 000 CN-токенов', '1 400 ⭐ для генерации медиа', 'Загрузка и обработка файлов', 'Поиск в интернете', 'Deep Research (глубокий анализ)', 'Голосовой ввод и вывод', 'Все топовые AI-модели', 'Генерация картинок, видео и музыки', 'ИИ-агенты для сложных задач'], popular: true },
  { id: 'promax', name: 'Комфорт', price: '1500', desc: 'Подписка на месяц — 23M токенов ежемесячно', tokens: 23_000_000, stars: 2_300, features: ['Подписка на 30 дней с ежемесячным обновлением', '23 000 000 CN-токенов каждый месяц', '2 300 ⭐ для генерации медиа каждый месяц', 'Загрузка и обработка файлов', 'Поиск в интернете', 'Deep Research (глубокий анализ)', 'Голосовой ввод и вывод', 'Все топовые AI-модели', 'Генерация картинок, видео и музыки', 'ИИ-агенты для сложных задач', 'Автоматическое продление'], popular: false, badge: 'Подписка' },
  { id: 'premium', name: 'Премиум', price: '2999', desc: 'Для профессионалов', tokens: 42_000_000, stars: 4_200, features: ['42 000 000 CN-токенов', '4 200 ⭐ для генерации медиа', 'Загрузка и обработка файлов', 'Поиск в интернете', 'Deep Research (глубокий анализ)', 'Голосовой ввод и вывод', 'Все топовые AI-модели', 'Генерация картинок, видео и музыки', 'ИИ-агенты для сложных задач'], popular: true },
  { id: 'max', name: 'Макс', price: '4999', desc: 'Для команд и экспертов', tokens: 70_000_000, stars: 7_000, features: ['70 000 000 CN-токенов', '7 000 ⭐ для генерации медиа', 'Загрузка и обработка файлов', 'Поиск в интернете', 'Deep Research (глубокий анализ)', 'Голосовой ввод и вывод', 'Все топовые AI-модели', 'Генерация картинок, видео и музыки', 'ИИ-агенты для сложных задач'], popular: false },
]

export default function Landing() {
  const navigate = useNavigate()
  const [emailInput, setEmailInput] = useState('')
  const { user } = useAuthStore()
  const [blocks, setBlocks] = useState<LandingBlock[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    client.get('/landing/blocks')
      .then(({ data }) => setBlocks(data || []))
      .catch(() => setBlocks([]))
      .finally(() => setLoaded(true))
  }, [])

  const getBlocks = (section: string): LandingBlock[] =>
    blocks.filter((b) => b.section === section && b.is_visible).sort((a, b) => a.sort_order - b.sort_order)

  const getBlock = (section: string, key: string): string =>
    blocks.find((b) => b.section === section && b.block_key === key && b.is_visible)?.content || ''

  const getExtra = (section: string, key: string): any => {
    const b = blocks.find((b2) => b2.section === section && b2.block_key === key && b2.is_visible)
    if (!b?.extra_data) return {}
    try { return JSON.parse(b.extra_data) } catch { return {} }
  }

  const renderText = (block: LandingBlock): string => {
    const fmt = block.format_type || 'p'
    const bold = block.is_bold
    let cls = ''
    if (fmt === 'h1') cls = 'text-5xl md:text-7xl font-bold mb-6 leading-tight'
    else if (fmt === 'h2') cls = 'text-3xl md:text-4xl font-bold gradient-text mb-3'
    else if (fmt === 'h3') cls = 'text-lg font-semibold text-white mb-2'
    else if (fmt === 'h4') cls = 'text-base font-semibold text-white mb-2'
    else if (fmt === 'blockquote') cls = 'italic border-l-2 border-indigo-400/30 pl-3 text-gray-400'
    else if (fmt === 'strong') cls = 'font-semibold'
    else cls = 'text-gray-500'
    if (bold && fmt === 'p') cls += ' font-bold'
    return cls
  }

  const resolveIcon = (name: string | null | undefined, size = 'w-6 h-6') => {
    if (!name) return <Sparkles className={size} />
    const Icon = lucideIcons[name]
    if (Icon) return <Icon className={size} />
    return <Sparkles className={size} />
  }

  return (
    <>
      <SEO canonical="/" />
      <div className="min-h-screen bg-[#0f0f1a]" style={{minHeight:2600}}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0f0f1a]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          {/* Первая строка: лого + Тарифы/Модели + кнопки */}
          <div className="h-16 flex items-center relative">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold gradient-text">Chat-Neuro</span>
            </Link>
            <nav className="hidden sm:flex items-center gap-2 absolute left-1/2 -translate-x-1/2">
              <Link to="/pricing" className="px-3 py-2 text-base text-white/80 hover:text-white transition-colors">Тарифы</Link>
              <Link to="/ai-models" className="px-3 py-2 text-base text-white/80 hover:text-white transition-colors">Модели</Link>
            </nav>
            <div className="flex items-center gap-2 ml-auto">
            {user ? (
              <Link to="/chat" className="gradient-btn px-5 py-2 rounded-xl text-sm inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                {user.name || 'Мой аккаунт'}
              </Link>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors">Войти</Link>
                <Link to="/register" className="gradient-btn px-5 py-2 rounded-xl text-sm">Регистрация</Link>
              </>
            )}
          </div>
        </div>
          {/* Вторая строка: Тарифы / Модели — по центру */}
          <div className="flex items-center justify-center gap-2 pb-2 sm:pb-0 sm:hidden">
            <Link to="/pricing" className="px-3 py-1.5 text-sm text-white/80 hover:text-white transition-colors">Тарифы</Link>
            <Link to="/ai-models" className="px-3 py-1.5 text-sm text-white/80 hover:text-white transition-colors">Модели</Link>
          </div>
        </div>
      </nav>

      <main>
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden min-h-[580px] max-md:min-h-[520px]">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/5 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-indigo-500/10 to-transparent rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-6 text-xs text-gray-400">
            <Star className="w-3 h-3 text-amber-400" />
            🤖 Все AI-модели в одном окне
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">Нейросеть онлайн — </span><span className="gradient-text">все AI модели в одном окне</span>
          </h1>

          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Официальные нейросети: GPT, Claude, DeepSeek, Gemini, Grok, Qwen — все возможности ИИ в едином интерфейсе без VPN и ограничений.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Введите email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="input-field flex-1"
            />
            <button
              onClick={() => {
                if (user) {
                  navigate('/chat')
                } else {
                  navigate(emailInput.trim() ? `/register?email=${encodeURIComponent(emailInput.trim())}` : '/register')
                }
              }}
              className="gradient-btn px-8 py-3 rounded-xl text-sm flex items-center gap-2 flex-shrink-0"
            >
              Начать бесплатно
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-gray-600 mt-3">👾 Бесплатные модели, генерация, безлимитный чат</p>
        </div>
      </section>

      {/* Services */}
      {(() => {
        const svcBlocks = getBlocks('services')
        const titleBlock = svcBlocks.find(b => b.block_key === 'services_title')
        const descBlock = svcBlocks.find(b => b.block_key === 'services_desc')
        const cards = svcBlocks.filter(b => b.block_key.startsWith('service_') && b.block_type === 'rich')

        // If no CMS cards, use fallback
        const displayCards = cards.length > 0
          ? cards.map(b => {
              try {
                const d = JSON.parse(b.content || '{}')
                return { ...d, icon: b.icon_name || d.icon, _key: b.block_key }
              } catch { return null }
            }).filter(Boolean)
          : fallbackServices

        if (!titleBlock && !descBlock && cards.length === 0) return null

        return (
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                {(titleBlock?.content || 'Все нейросети в одном месте') && (
                  <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-3">
                    {titleBlock?.content || 'Все нейросети в одном месте'}
                  </h2>
                )}
                {(descBlock?.content || 'Чат с нейросетью, генерация изображений и видео') && (
                  <p className="text-gray-500 max-w-xl mx-auto">{descBlock?.content || 'Чат с нейросетью, генерация изображений, видео, музыки — 10 AI сервисов в одном окне'}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayCards.map((svc: any, idx: number) => (
                  <div
                    key={svc._key || idx}
                    className={`glass-card p-6 group cursor-pointer border ${svc.borderColor || 'border-white/10'} ${svc.bgColor || ''}`}
                    onClick={() => navigate(user ? (svc.path || '/chat') : '/register')}
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${svc.color || 'from-indigo-500 to-purple-600'} p-2.5 mb-4`}>
                      {resolveIcon(svc.icon, 'w-full h-full text-white')}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-indigo-300 transition-colors">{svc.title}</h3>
                    <p className="text-sm text-gray-400">{svc.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )
      })()}

{/* Pricing */}
      {(() => {
        const pricingBlocks = getBlocks('pricing')
        const titleBlock = pricingBlocks.find(b => b.block_key === 'pricing_title')
        const descBlock = pricingBlocks.find(b => b.block_key === 'pricing_desc')
        const cards = pricingBlocks.filter(b => b.block_key.startsWith('plan_') && b.block_type === 'rich')
        const displayPlans = cards.length > 0
          ? cards.map(b => {
              try { return JSON.parse(b.content || '{}') }
              catch { return null }
            }).filter(Boolean)
          : fallbackPlans

        if (pricingBlocks.length === 0) return null

        return (
          <section className="py-16 px-4" id="pricing">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                {(titleBlock?.content || 'Выберите тариф') && (
                  <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-3">{titleBlock?.content || 'Выберите тариф'}</h2>
                )}
                {descBlock?.content && <p className="text-gray-500">{descBlock.content}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayPlans.map((plan: any, idx: number) => (
                  <div
                    key={plan.name || idx}
                    className={`glass-card p-6 relative ${plan.popular ? 'border-indigo-500/30 ring-1 ring-indigo-500/20' : ''}`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-xs font-semibold text-white">
                        Популярный
                      </div>
                    )}
                    {plan.badge && !plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-sky-600 to-teal-600 text-xs font-semibold text-white">
                        {plan.badge}
                      </div>
                    )}

                    <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
                    <p className="text-xs text-gray-500 mb-4">{plan.desc}</p>
                    {plan.id !== 'promax' && plan.id !== 'free' && (
                      <p className="text-[9px] text-gray-500 -mt-3 mb-3">Разовое пополнение</p>
                    )}
                    {plan.id === 'promax' && (
                      <p className="text-[9px] text-sky-400 -mt-3 mb-3">🔄 Ежемесячная подписка</p>
                    )}

                    <div className="mb-6">
                      {plan.id !== 'free' && (
                        <>
                          <span className="text-4xl font-bold text-white">{plan.price}</span>
                          <span className="text-gray-500"> ₽</span>
                        </>
                      )}
                      {plan.id === 'free' && (
                        <span className="text-4xl font-bold text-white">0</span>
                      )}
                    </div>

                    {plan.tokens !== undefined && plan.tokens > 0 && (
                      <div className="mb-4 space-y-1 text-center">
                        <p className="text-lg font-bold text-amber-300">{plan.tokens.toLocaleString()}</p>
                        <p className="text-[10px] text-gray-500">CN-токенов</p>
                        {plan.stars > 0 && (
                          <p className="text-sm text-white font-semibold">
                            или ✦ {plan.stars.toLocaleString()} звёзд
                            <span className="text-[10px] text-white/70 block mt-0.5">(для генерации картинок/фото, видео и музыки)</span>
                          </p>
                        )}
                      </div>
                    )}

                    <ul className="space-y-2 mb-3">
                      {(plan.features || []).map((feat: string, fi: number) => (
                        <li key={fi} className="flex items-center gap-2 text-sm text-white/80">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                    {plan.id === 'free' && plan.services && plan.services.length > 0 && (
                      <>
                        <div className="border-t border-white/10 my-3" />
                        <ul className="space-y-2 mb-6">
                          {plan.services.map((feat: string, fi: number) => (
                            <li key={fi} className="flex items-center gap-2 text-sm text-gray-400">
                              <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                              {feat}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    <button
                      onClick={() => navigate(user ? (plan.price === '0' ? '/chat' : '/payment') : '/register')}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${plan.popular ? 'gradient-btn' : 'glass text-gray-300 hover:bg-white/10 hover:text-white'}`}
                    >
                      {plan.price === '0' ? 'Бесплатно' : `${plan.price} ₽`}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )
      })()}

      {/* Features */}
      {(() => {
        const featBlocks = getBlocks('features')
        const titleBlock = featBlocks.find(b => b.block_key === 'features_title')
        const cards = featBlocks.filter(b => b.block_key.startsWith('feature_') && b.block_type === 'rich')
        const displayCards = cards.length > 0
          ? cards.map(b => {
              try {
                const d = JSON.parse(b.content || '{}')
                // icon_name из БД (поле) приоритетнее, чем icon из JSON в content
                const iconName = b.icon_name || d.icon || 'Zap'
                return { icon: iconName, title: d.title, desc: d.desc, _key: b.block_key }
              } catch { return null }
            }).filter(Boolean)
          : fallbackFeatures

        if (!titleBlock && cards.length === 0) return null

        return (
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                {(titleBlock?.content || 'Почему Chat-Neuro.ru?') && (
                  <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-3">
                    {titleBlock?.content || 'Почему Chat-Neuro.ru?'}
                  </h2>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {displayCards.map((feat: any, idx: number) => (
                    <div key={feat._key || idx} className="glass-card p-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-2.5 mx-auto mb-4">
                        {resolveIcon(feat.icon, 'w-full h-full text-white')}
                      </div>
                      <h3 className="text-sm font-semibold text-white mb-2">{feat.title}</h3>
                      <p className="text-xs text-gray-400">{feat.desc}</p>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        )
      })()}

      {/* Stats */}
      {(() => {
        const statBlocks = getBlocks('stats')
        const titleBlock = statBlocks.find(b => b.block_key === 'stats_title')
        const cards = statBlocks.filter(b => b.block_key.startsWith('stat_') && b.block_type === 'rich')
        const displayStats = cards.length > 0
          ? cards.map(b => {
              try {
                return JSON.parse(b.content || '{}')
              } catch { return null }
            }).filter(Boolean)
          : [{ value: '50+', label: 'Моделей ИИ' }, { value: '10K+', label: 'Активных пользователей' }, { value: '1M+', label: 'Сгенерировано запросов' }, { value: '99.9%', label: 'Аптайм' }]

        if (statBlocks.length === 0) return null

        return (
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto">
              {titleBlock?.content && (
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-3">{titleBlock.content}</h2>
                </div>
              )}
              <div className="glass-card p-8 md:p-12">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                  {displayStats.map((stat: any, idx: number) => (
                    <div key={idx}>
                      <div className="text-3xl md:text-4xl font-bold gradient-text mb-1">{stat.value}</div>
                      <div className="text-xs text-gray-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )
      })()}
      {/* SEO Text Block */}
      {(() => {
        const seoBlocks = getBlocks('seo_text')
        const titleBlock = seoBlocks.find(b => b.block_key === 'seo_title')
        const paragraphs = seoBlocks.filter(b => b.block_key.startsWith('seo_paragraph_') && b.content).map(b => b.content)

        if (seoBlocks.length === 0) return null

        return (
          <section className="py-16 px-4 border-t border-white/5">
            <div className="max-w-4xl mx-auto">
              {titleBlock?.content && (
                <h2 className="text-2xl md:text-3xl font-bold gradient-text mb-8 text-center">{titleBlock.content}</h2>
              )}
              <div className="space-y-4 text-gray-400 leading-relaxed text-sm md:text-base">
                {paragraphs.map((text, i) => (
                  <p key={i}>{text}</p>
                ))}
              </div>
            </div>
          </section>
        )
      })()}

      {/* CTA */}
      {(() => {
        const ctaBlocks = getBlocks('cta')
        const titleBlock = ctaBlocks.find(b => b.block_key === 'cta_title')
        const descBlock = ctaBlocks.find(b => b.block_key === 'cta_desc')
        const btnBlock = ctaBlocks.find(b => b.block_key === 'cta_button')

        if (ctaBlocks.length === 0) return null

        return (
          <section className="py-20 px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="glass-card p-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5" />
                <div className="relative">
                  {titleBlock?.content && <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">{titleBlock.content}</h2>}
                  {descBlock?.content && <p className="text-gray-400 mb-8 max-w-lg mx-auto">{descBlock.content}</p>}
                  <button
                    onClick={() => {
                      const href = getExtra('cta', 'cta_button').href
                      if (href) window.open(href, '_blank')
                      else navigate(user ? '/chat' : '/register')
                    }}
                    className="gradient-btn px-10 py-3.5 rounded-xl text-base inline-flex items-center gap-2"
                  >
                    {btnBlock?.content || 'Начать бесплатно'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>
        )
      })()}

      {/* FAQ Block */}
      {(() => {
        const faqBlocks = getBlocks('faq')
        const titleBlock = faqBlocks.find(b => b.block_key === 'faq_title')
        const questions = faqBlocks.filter(b => b.block_key.match(/^faq_q\d+$/) && b.block_type === 'rich')

        if (faqBlocks.length === 0) return null

        return (
          <section className="py-16 px-4 border-t border-white/5">
            <div className="max-w-3xl mx-auto">
              {titleBlock?.content && (
                <h2 className="text-2xl md:text-3xl font-bold gradient-text mb-10 text-center">{titleBlock.content}</h2>
              )}
              <div className="space-y-3">
                {questions.map((qBlock, i) => {
                  let data: {q?: string; a?: string} = {}
                  try { data = JSON.parse(qBlock.content || '{}') } catch {}
                  if (!data.q || !data.a) return null
                  return (
                    <details key={i} className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                      <summary className="px-5 py-4 cursor-pointer text-sm md:text-base font-medium text-gray-200 hover:text-white transition-colors flex items-center justify-between">
                        <span>{data.q}</span>
                        <ChevronDown className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform shrink-0" />
                      </summary>
                      <div className="px-5 pb-4 text-sm text-gray-400 leading-relaxed border-t border-white/5 pt-3">
                        {data.a}
                      </div>
                    </details>
                  )
                })}
              </div>
            </div>
          </section>
        )
      })()}

      </main>
      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <span className="text-xl font-semibold gradient-text">{getBlock('footer', 'footer_logo') || 'Chat-Neuro.ru'}</span>
          </div>
          <div className="footer-links flex items-center justify-center flex-wrap gap-x-4 gap-y-0.5 text-base text-white/80 max-w-full">
            {(() => {
              const footerBlocks = getBlocks('footer')
              const year = getBlock('footer', 'footer_year') || '2026'
              const terms = getBlock('footer', 'footer_terms') || 'Условия'
              const privacy = getBlock('footer', 'footer_privacy') || 'Конфиденциальность'
              const support = getBlock('footer', 'footer_support') || 'Поддержка'
              return <>
                <span className="whitespace-nowrap">© {year} {getBlock('footer', 'footer_logo') || 'Chat-Neuro.ru'}</span>
                <Link to="/pricing" className="hover:text-gray-400 transition-colors whitespace-nowrap">Тарифы</Link>
                <Link to="/ai-models" className="hover:text-gray-400 transition-colors whitespace-nowrap">Модели</Link>
                <Link to="/faq" className="hover:text-gray-400 transition-colors whitespace-nowrap">FAQ</Link>
                <Link to="/terms" className="hover:text-gray-400 transition-colors whitespace-nowrap">{terms}</Link>
                <Link to="/privacy" className="hover:text-gray-400 transition-colors whitespace-nowrap">{privacy}</Link>
                <Link to="/support" className="hover:text-gray-400 transition-colors whitespace-nowrap">{support}</Link>
              </>
            })()}
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}
